
import { Alert } from "@/components/ui/Alert";
import { lazy, Suspense, useEffect } from "react";
import { resetModelCache } from "../engine/modelCache";
import { useSandbox } from "../hooks/useSandbox";
import { useSoundtrack } from "../hooks/useSoundtrack";
import { useImmersiveUi } from "../hooks/useImmersiveUi";
import { useEditMode } from "../hooks/useEditMode";
import { DragController } from "./DragController";
import { FloatingToolbar } from "./FloatingToolbar";
import { ImmersiveToolbar } from "./ImmersiveToolbar";
import { LoadingOverlay } from "./LoadingOverlay";
import { MobileFAB } from "./MobileFAB";
import { OrientationHint } from "./OrientationHint";
import { SidePanel } from "./SidePanel";
import { TransformModeToggle } from "./TransformModeToggle";

const SandboxCanvas = lazy(() =>
  import("../engine/SandboxCanvas").then((mod) => ({
    default: mod.SandboxCanvas,
  })),
);

export function SandboxApp() {
  const {
    prompt,
    isLoading,
    loadingMessage,
    activeSpaceName,
    error,
    setPrompt,
    setError,
    handleGenerate,
  } = useSandbox();

  const { isReadonly } = useEditMode();

  // Mount soundtrack audio engine (creates audio element, syncs with scene)
  useSoundtrack();

  // Full model cache reset on unmount (navigating away from sandbox)
  useEffect(() => {
    return () => resetModelCache();
  }, []);

  const {
    immersiveToolbarMinimized,
    immersiveInputFocused,
    toggleImmersiveToolbarMinimized,
    setImmersiveInputFocused,
  } = useImmersiveUi();

  return (
    <div className="h-screen w-screen flex bg-sands-50 overflow-hidden">
      <div className="flex-1 min-w-0 relative h-full overflow-hidden">
        <Suspense fallback={null}>
          <SandboxCanvas />
        </Suspense>

        <FloatingToolbar />

        <TransformModeToggle />

        {!isReadonly && (
          <ImmersiveToolbar
            spaceName={activeSpaceName}
            prompt={prompt}
            onPromptChange={setPrompt}
            onSubmit={handleGenerate}
            isLoading={isLoading}
            isMinimized={immersiveToolbarMinimized}
            inputFocused={immersiveInputFocused}
            onToggleMinimize={toggleImmersiveToolbarMinimized}
            onInputFocus={() => setImmersiveInputFocused(true)}
            onInputBlur={() => setImmersiveInputFocused(false)}
            isReadonly={isReadonly}
          />
        )}

        <DragController />

        {/* Mobile-only: FAB for side panel + orientation hint */}
        <MobileFAB />
        <OrientationHint />

        {isLoading && <LoadingOverlay message={loadingMessage} />}

        {error && (
          <Alert
            variant="destructive"
            className="absolute top-20 left-1/2 -translate-x-1/2 shadow-sm z-30 flex items-center gap-3"
          >
            {error}
            <button
              onClick={() => setError(null)}
              className="text-destructive/60 hover:text-destructive"
            >
              x
            </button>
          </Alert>
        )}
      </div>

      {/* Side panel - on desktop only (mobile renders as overlay via SidePanel itself) */}
      <div className="relative h-full hidden md:block">
        <SidePanel />
      </div>

      {/* Side panel overlay for mobile (rendered outside the main container for full-screen coverage) */}
      <div className="md:hidden">
        <SidePanel />
      </div>
    </div>
  );
}
